/**
 * PatientDataEditModal - Modal for editing patient data during check-in
 * 
 * Handles different field types and tracks history changes for:
 * - Name (firstName/lastName) - adds old name to akaNames
 * - DOB - adds old DOB to previousDOBs with confirmation
 * - Other fields - simple edit
 */

import React, { useState, useEffect } from 'react';
import { tokens } from '@healthdesk/shared-utils';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Close, Warning, History, InfoOutlined } from '@mui/icons-material';

/**
 * Determine field category based on dataPath
 */
const getFieldCategory = (dataPath) => {
  if (dataPath?.includes('firstName') || dataPath?.includes('lastName')) {
    return 'name';
  }
  if (dataPath?.includes('dateOfBirth') || dataPath?.includes('dob')) {
    return 'dob';
  }
  if (dataPath?.includes('sex') || dataPath?.includes('gender')) {
    return 'sex';
  }
  if (dataPath?.includes('phone')) {
    return 'phone';
  }
  if (dataPath?.includes('email')) {
    return 'email';
  }
  if (dataPath?.includes('address')) {
    return 'address';
  }
  if (dataPath?.includes('emergency')) {
    return 'emergencyContact';
  }
  return 'other';
};

/**
 * Format date for input field (YYYY-MM-DD)
 * No Date parsing - treats as plain string to avoid timezone issues
 */
const formatDateForInput = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    // If it's already ISO format YYYY-MM-DD, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    // If it's in format like "1/1/1961" or "01/01/1961", convert to YYYY-MM-DD
    const mdyMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdyMatch) {
      const [, month, day, year] = mdyMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }
  // For any other format, return as-is (the input will handle it)
  return String(value);
};

/**
 * Format date for display to user (more readable)
 * No Date parsing - manually format from YYYY-MM-DD string
 */
const formatDateDisplay = (value) => {
  if (!value) return 'Not set';
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Handle YYYY-MM-DD format
  const isoMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const monthIndex = parseInt(month, 10) - 1;
    const dayNum = parseInt(day, 10);
    return `${monthNames[monthIndex]} ${dayNum}, ${year}`;
  }
  
  // Handle M/D/YYYY or MM/DD/YYYY format
  const mdyMatch = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    const [, month, day, year] = mdyMatch;
    const monthIndex = parseInt(month, 10) - 1;
    const dayNum = parseInt(day, 10);
    return `${monthNames[monthIndex]} ${dayNum}, ${year}`;
  }
  
  // Return as-is if format not recognized
  return value;
};

const PatientDataEditModal = ({
  open,
  onClose,
  field,
  currentValue,
  originalValue,
  onSave,
  isFirstEntry = false,
  akaNames = [],
  previousDOBs = [],
}) => {
  const [editedValue, setEditedValue] = useState('');
  const [error, setError] = useState('');
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  
  const fieldCategory = getFieldCategory(field?.dataPath);
  const isNameField = fieldCategory === 'name';
  const isDOBField = fieldCategory === 'dob';
  const requiresConfirmation = isDOBField;
  
  // Initialize edited value when modal opens
  useEffect(() => {
    if (open && field) {
      if (isDOBField) {
        setEditedValue(formatDateForInput(currentValue));
      } else {
        setEditedValue(currentValue || '');
      }
      setError('');
      setConfirmationChecked(false);
    }
  }, [open, field, currentValue, isDOBField]);

  const handleSave = () => {
    // Validation
    if (!editedValue && field?.required) {
      setError('This field is required');
      return;
    }

    if (requiresConfirmation && !confirmationChecked) {
      setError('Please confirm the information is correct');
      return;
    }

    // Email validation
    if (fieldCategory === 'email' && editedValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editedValue)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    // Phone validation
    if (fieldCategory === 'phone' && editedValue) {
      const phoneRegex = /^[\d\s\-\(\)\+]{10,}$/;
      if (!phoneRegex.test(editedValue.replace(/\D/g, '')) && editedValue.replace(/\D/g, '').length < 10) {
        setError('Please enter a valid phone number');
        return;
      }
    }

    // DOB validation
    if (isDOBField && editedValue) {
      const dobDate = new Date(editedValue);
      const today = new Date();
      if (dobDate > today) {
        setError('Date of birth cannot be in the future');
        return;
      }
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 150);
      if (dobDate < minDate) {
        setError('Please enter a valid date of birth');
        return;
      }
    }

    // Determine if value actually changed
    const valueChanged = isDOBField 
      ? formatDateForInput(editedValue) !== formatDateForInput(originalValue)
      : editedValue !== originalValue;

    onSave({
      field,
      newValue: editedValue,
      originalValue,
      valueChanged,
      category: fieldCategory,
    });
    
    onClose();
  };

  const handleClose = () => {
    setError('');
    setEditedValue('');
    setConfirmationChecked(false);
    onClose();
  };

  if (!field) return null;

  const renderFieldInput = () => {
    if (isDOBField) {
      return (
        <TextField
          fullWidth
          type="date"
          label="Date of Birth"
          value={editedValue}
          onChange={(e) => {
            setEditedValue(e.target.value);
            setError('');
          }}
          error={!!error}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: new Date().toISOString().split('T')[0],
          }}
          sx={{ mt: 2 }}
        />
      );
    }

    if (fieldCategory === 'sex') {
      return (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Sex</InputLabel>
          <Select
            value={editedValue || ''}
            onChange={(e) => {
              setEditedValue(e.target.value);
              setError('');
            }}
            label="Sex"
          >
            <MenuItem value="">Not specified</MenuItem>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        label={field.label}
        value={editedValue}
        onChange={(e) => {
          setEditedValue(e.target.value);
          setError('');
        }}
        error={!!error}
        multiline={field.dataType === 'textarea'}
        rows={field.dataType === 'textarea' ? 3 : 1}
        type={fieldCategory === 'email' ? 'email' : fieldCategory === 'phone' ? 'tel' : 'text'}
        sx={{ mt: 2 }}
      />
    );
  };

  const renderHistoryInfo = () => {
    if (isNameField && akaNames?.length > 0) {
      return (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <History fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary">
              Previous Names (AKA)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {akaNames.map((aka, index) => (
              <Chip 
                key={index} 
                label={`${aka.firstName} ${aka.lastName}`}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      );
    }

    if (isDOBField && previousDOBs?.length > 0) {
      return (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <History fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary">
              Previous Dates of Birth
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {previousDOBs.map((dob, index) => (
              <Chip 
                key={index} 
                label={formatDateDisplay(dob)}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      );
    }

    return null;
  };

  const renderChangeWarning = () => {
    const showWarning = editedValue !== currentValue && currentValue;

    if (!showWarning) return null;

    if (isNameField) {
      return (
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2 }}>
          <Typography variant="body2">
            Changing your name will add your current name to your "Also Known As" (AKA) history. 
            Healthcare providers will be able to see your previous names for identification purposes.
          </Typography>
        </Alert>
      );
    }

    if (isDOBField) {
      return (
        <>
          {/* clinical severity: medium — identity verification context */}
          <Alert
            icon={<Warning sx={{ color: 'common.white' }} />}
            sx={{
              mt: 2,
              bgcolor: tokens.clinical.medium.light,
              color: 'common.white',
              '& .MuiAlert-icon': { color: 'common.white' },
            }}
          >
            <Typography variant="body2">
              Changing your date of birth will add your current DOB to your history. 
              Healthcare providers will be able to see this change for verification purposes.
            </Typography>
          </Alert>
        </>
      );
    }

    return null;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          Edit {field.label}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Current Value Display */}
        {currentValue && (
          <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Current Value
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {isDOBField ? formatDateDisplay(currentValue) : currentValue}
            </Typography>
          </Box>
        )}

        {/* History Info */}
        {renderHistoryInfo()}

        {/* Edit Input */}
        {renderFieldInput()}

        {/* Change Warning */}
        {renderChangeWarning()}

        {/* Confirmation Checkbox for DOB */}
        {isDOBField && (
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(e) => {
                    setConfirmationChecked(e.target.checked);
                    if (e.target.checked) setError('');
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  {isFirstEntry 
                    ? "I confirm this date of birth is correct"
                    : "I confirm this change to my date of birth is correct"
                  }
                </Typography>
              }
            />
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={requiresConfirmation && !confirmationChecked}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDataEditModal;
